import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import type { NextPage } from "next"
import clsx from "clsx"

import { useSearchCMSQuery } from "~/api/interruptor/search/query"
import { Input } from "~/components/Input"
import { Loading } from "~/components/Loading"
import { PublishedItemPreview } from "~/components/PublishedItemPreview"
import { SEO } from "~/components/SEO"
import { Waypoint } from "~/components/Waypoint"

import { mapPagesToPreviewPropsList } from "./helpers"
import { useAllArticlesAndEpisodesQuery } from "./query"

export const ArticlesList: NextPage = () => {
  const { t } = useTranslation("articles")
  const [searchText, setSearchText] = useState("")

  const { data: searchResults, isLoading } = useSearchCMSQuery(
    { searchText },
    {
      onError: () => {
        toast.error(t("error.fetchingArticles"))
      },
    },
  )

  const query = useAllArticlesAndEpisodesQuery(
    { pageSize: 6 },
    {
      onError: () => {
        toast.error(t("error.fetchingArticles"))
      },
      select: ({ pages, pageParams }) => ({
        pages: mapPagesToPreviewPropsList(pages),
        pageParams,
      }),
    },
  )
  const nodes = query.data?.pages || []
  const isUserSearching = isLoading || searchResults

  return (
    <>
      <SEO pageTitle={t("pageTitle")} />
      <section className="m-auto mb-8 max-w-screen-lg md:p-4">
        <div className={clsx("bg-primary-500 text-white", "py-6 px-4 md:p-8")}>
          <h1 className={clsx("text-2xl md:text-4xl")}>{t("pageTitle")}</h1>
        </div>

        <div className="flex flex-col items-center p-6">
          <div className="w-full max-w-sm">
            <Input
              name="q"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              label={t("search.input.label")}
              placeholder={t("search.input.placeholder")}
            />
            {!!searchResults && !searchResults.length && (
              <p className="my-2 font-display italic">
                {t("search.noResults")}
              </p>
            )}
          </div>
        </div>

        <ul className="mb-6 grid gap-4 sm:grid-cols-2 md:my-8 md:grid-cols-3">
          {!!searchResults &&
            searchResults.map((result) => {
              const { href, title, description, image, datePublished } = result
              return (
                <li key={href} className="list-none">
                  <PublishedItemPreview
                    href={href}
                    title={title}
                    description={description}
                    image={image}
                    date={datePublished}
                  />
                </li>
              )
            })}

          {!isUserSearching &&
            nodes.map((preview) => {
              return (
                <li key={preview.href} className="list-none">
                  <PublishedItemPreview {...preview} />
                </li>
              )
            })}
        </ul>

        {(isLoading || query.isLoading || query.isFetchingNextPage) && (
          <Loading className="mx-auto" />
        )}

        {!searchResults && query.hasNextPage && (
          <Waypoint
            key={query.dataUpdatedAt}
            onEnter={() => {
              void query.fetchNextPage()
            }}
          />
        )}
      </section>
    </>
  )
}
